import React from 'react';

const MaintenancePage = () => {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #0a1f13 0%, #0d2818 30%, #112d1e 60%, #0a1f13 100%)' }}>
      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />
      
      {/* Soft glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #22c55e 0%, transparent 70%)' }} />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        {/* Animated leaf icon */}
        <div className="mb-10 relative">
          <div className="w-24 h-24 rounded-3xl bg-emerald-900/40 border border-emerald-700/20 flex items-center justify-center backdrop-blur-sm"
            style={{ animation: 'float 6s ease-in-out infinite' }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c1.5 0 3-.3 4.3-.9" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M17 3c-2 2-4.5 6-4.5 9.5 0 2.5 1.5 4.5 4 5.5" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M22 2c-2 2-6 4-9.5 4" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M17 3c1.5 2 2.5 5 2.5 8" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Brand */}
        <p className="text-emerald-500/60 text-xs tracking-[0.3em] uppercase font-medium mb-6">
          AquaPlants Plus
        </p>

        {/* Main heading */}
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold text-center mb-5 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Under Maintenance
        </h1>

        {/* Divider */}
        <div className="w-16 h-[2px] bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent mb-6" />

        {/* Subtext */}
        <p className="text-emerald-100/50 text-base sm:text-lg text-center max-w-md leading-relaxed mb-10">
          We're tending to a few things behind the scenes. 
          The shop will be back up and running shortly.
        </p>

        {/* Status indicator */}
        <div className="flex items-center gap-3 bg-emerald-950/60 border border-emerald-800/20 rounded-full px-6 py-3 backdrop-blur-sm">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          <span className="text-emerald-300/80 text-sm font-medium tracking-wide">Working on it</span>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
      `}</style>
    </div>
  );
};

const ClosedForTodayPage = () => {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #111318 0%, #161922 30%, #1a1d28 60%, #111318 100%)' }}>
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,.3) 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} />

      {/* Warm glow top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-[0.07]"
        style={{ background: 'radial-gradient(ellipse, #f59e0b 0%, transparent 70%)' }} />

      {/* Cool glow bottom */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] opacity-[0.05]"
        style={{ background: 'radial-gradient(ellipse, #6366f1 0%, transparent 70%)' }} />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        {/* Moon icon */}
        <div className="mb-10 relative">
          <div className="w-24 h-24 rounded-3xl bg-amber-900/20 border border-amber-700/15 flex items-center justify-center backdrop-blur-sm"
            style={{ animation: 'glow 4s ease-in-out infinite' }}>
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="7.5" cy="10" r="0.5" fill="#fbbf24" opacity="0.4"/>
              <circle cx="10" cy="15" r="0.5" fill="#fbbf24" opacity="0.3"/>
              <circle cx="14" cy="8" r="0.5" fill="#fbbf24" opacity="0.2"/>
            </svg>
          </div>
        </div>

        {/* Brand */}
        <p className="text-amber-400/40 text-xs tracking-[0.3em] uppercase font-medium mb-6">
          AquaPlants Plus
        </p>

        {/* Main heading */}
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold text-center mb-5 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Closed for Today
        </h1>

        {/* Divider */}
        <div className="w-16 h-[2px] bg-gradient-to-r from-transparent via-amber-500/40 to-transparent mb-6" />

        {/* Subtext */}
        <p className="text-gray-400 text-base sm:text-lg text-center max-w-md leading-relaxed mb-4">
          We've wrapped up for the day.
          Come back tomorrow and browse our fresh collection.
        </p>

        {/* Store hours hint */}
        <p className="text-gray-500/60 text-sm text-center mb-10">
          Springfield, MO — locally grown, shipped with care
        </p>

        {/* CTA pill */}
        <div className="flex items-center gap-3 bg-white/[0.04] border border-white/[0.06] rounded-full px-6 py-3 backdrop-blur-sm">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          <span className="text-gray-300/70 text-sm font-medium">See you tomorrow</span>
        </div>
      </div>

      <style>{`
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(251, 191, 36, 0); }
          50% { box-shadow: 0 0 30px rgba(251, 191, 36, 0.08); }
        }
      `}</style>
    </div>
  );
};

export { MaintenancePage, ClosedForTodayPage };
